/**
 * FP3-062 follow-up (Jun 1, 2026) — Inline provider type editor for
 * Workspace Info.
 *
 * Surfaced to admin family + solo provider OWNER. Reads the
 * canonical option list from the auth-gated endpoint
 * `/api/account/provider-role-options` and writes via PATCH
 * `/api/account/provider-role`.
 *
 * Display rules:
 *   - When `provider.role` (canonical) OR legacy `provider.provider_type`
 *     is set → show the value + a Change pencil.
 *   - When neither is set → show "Set your provider type" CTA.
 *
 * After save we call `onChanged()` so the parent re-fetches the
 * workspace-info payload and the new role flows everywhere.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ProviderRoleEditor({ provider, canEdit, onChanged }) {
  const currentRole = (provider?.role || provider?.provider_type || "").trim();
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState([]);
  const [draft, setDraft] = useState(currentRole);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(currentRole);
  }, [currentRole]);

  useEffect(() => {
    if (!editing) return;
    api
      .getProviderRoleOptions()
      .then((r) => setOptions(Array.isArray(r.data?.options) ? r.data.options : []))
      .catch(() => setOptions([]));
  }, [editing]);

  if (!canEdit) {
    return (
      <span data-testid="provider-type-readonly">
        {currentRole || "Provider type not set."}
      </span>
    );
  }

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2" data-testid="provider-type-value">
        <span>{currentRole || "Provider type not set."}</span>
        <button
          type="button"
          data-testid="provider-type-edit"
          onClick={() => setEditing(true)}
          title={currentRole ? "Change provider type" : "Set provider type"}
          aria-label={currentRole ? "Change provider type" : "Set provider type"}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#F3F1EC] transition-colors"
        >
          <PencilSimple size={13} />
        </button>
      </span>
    );
  }

  const save = async () => {
    const next = (draft || "").trim();
    if (!next) {
      toast.error("Please pick a provider type");
      return;
    }
    if (next === currentRole) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.updateProviderRoleSelf({ role: next });
      toast.success("Provider type updated");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update provider type");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid="provider-type-editor">
      <select
        data-testid="provider-type-select"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={busy}
        className="h-8 px-2 text-[12px] border border-[#E5E3DB] rounded-md bg-white text-[#1F2937] focus:outline-none focus:ring-1 focus:ring-[#1E3F33]"
      >
        <option value="">Pick one…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <Button
        type="button"
        size="sm"
        onClick={save}
        disabled={busy}
        className="h-8 bg-[#1E3F33] hover:bg-[#173127] text-white text-[11px] px-3"
        data-testid="provider-type-save"
      >
        <FloppyDisk size={12} className="mr-1" />
        Save
      </Button>
      <button
        type="button"
        onClick={() => { setDraft(currentRole); setEditing(false); }}
        disabled={busy}
        title="Cancel"
        aria-label="Cancel"
        data-testid="provider-type-cancel"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#F3F1EC] transition-colors"
      >
        <X size={13} />
      </button>
    </div>
  );
}
